import React from "react";
import { useParams, Link } from "react-router-dom";
import Loader from "@components/Loader";
import {
  useOrderQuery,
  useOrderProductsQuery,
  useOrderTotalsQuery,
  useOrderNotesQuery,
  useOrderHistoryQuery,
} from "@queries/order";
import { useCustomerHistoryQuery } from "@queries/customers";
import OrderSummary from "@components/orders/OrderSummary";
import OrderAddresses from "@components/orders/OrderAddresses";
import OrderProducts from "@components/orders/OrderProducts";
import OrderTotals from "@components/orders/OrderTotals";
import OrderNotes from "@components/orders/OrderNotes";
import OrderChecklist from "@components/orders/OrderChecklist";
import OrderServiceTime from "@components/orders/OrderServiceTime";
//import "@styles/admin-theme.css";

export default function OrderInfoPage() {
  const { id } = useParams();
  const orderQ = useOrderQuery(id);
  const prodsQ = useOrderProductsQuery(id);
  const totalsQ = useOrderTotalsQuery(id);
  const notesQ = useOrderNotesQuery(id);
  const histQ = useOrderHistoryQuery(id);

  const loading = orderQ.isLoading || prodsQ.isLoading || totalsQ.isLoading;

  const order = orderQ.data;
  const customerId = order?.customer_id;
  const customerHistoryQ = useCustomerHistoryQuery(customerId);

  if (loading) return <Loader />;
  if (orderQ.isError) return <div className="content"><div className="card">Error: {orderQ.error.message}</div></div>;

  return (
    <div className="content">
      <div className="header">
        <h1>Order #{order?.id ?? id}</h1>
        <nav className="bc">
          <Link to="/orders">Orders</Link> <span className="sep">›</span>
          <span>Order Info</span>
        </nav>
      </div>

      {/* Self-service order (website or mobile app) that the customer never
          confirmed/paid — empty payment method. Warn ops to verify before
          processing. Back-office orders are excluded: staff create those
          deliberately and take payment offline.

          `android`/`ios` were added when the v2 API started tagging the real
          sales channel instead of labelling every order "website". */}
      {["website", "web", "android", "ios"].includes(order?.platform) &&
        !String(order?.payment_method || "").trim() && (
        <div style={{
          background: "#fee2e2", border: "2px solid #dc2626", color: "#991b1b",
          borderRadius: 12, padding: "16px 20px", marginBottom: 20,
          display: "flex", alignItems: "center", gap: 14,
        }} role="alert">
          <span style={{ fontSize: 28, lineHeight: 1 }}>⚠️</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: 17, letterSpacing: ".3px" }}>
              {["website", "web"].includes(order?.platform) ? "WEBSITE" : "APP"} ORDER — NOT CONFIRMED
            </div>
            <div style={{ fontWeight: 500, fontSize: 13, marginTop: 4, color: "#b91c1c" }}>
              Placed on the {["website", "web"].includes(order?.platform) ? "website" : `mobile app (${order?.platform})`} but the
              customer did not complete payment / confirm the order. Call the customer to confirm before processing.
            </div>
          </div>
        </div>
      )}

      <OrderSummary order={order} />

      {order?.comment?.trim() && (
        <div className="card" style={{ marginBottom: "20px" }}>
          <div className="card-header">
            <h2>Customer Note</h2>
          </div>
          <div className="card-body">
            <div
              dir="auto"
              style={{ fontSize: "13px", whiteSpace: "pre-line" }}
            >
              {order.comment}
            </div>
          </div>
        </div>
      )}

      <OrderServiceTime order={order} />

      <OrderAddresses order={order} />

      <OrderChecklist orderId={id} />

      <OrderProducts orderId={id} products={prodsQ.data || []} />

      <OrderTotals orderId={id} order={order} totals={totalsQ.data || {}} products={prodsQ.data || []} />

      {customerHistoryQ.data?.data && customerHistoryQ.data.data.filter(h => !h.archived).length > 0 && (
        <div className="card" style={{ marginBottom: "20px" }}>
          <div className="card-header">
            <h2>Customer Special Notes</h2>
          </div>
          <div className="card-body">
            <div style={{ fontSize: "12px", whiteSpace: "pre-line" }}>
              {customerHistoryQ.data.data
                .filter((h) => !h.archived)
                .map((h) => `- ${h.comment}`)
                .join("\n")}
            </div>
          </div>
        </div>
      )}

      <OrderNotes
        orderId={id}
        notes={notesQ.data || []}
        history={histQ.data || []}
      />
    </div>
  );
}

